import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserIcon, MailIcon, LockIcon, LogOutIcon, ArrowRightIcon, CheckCircleIcon, RocketIcon, BookOpenIcon, SparklesIcon, ShieldCheckIcon, KeyIcon, PhoneIcon, MapPinIcon, CalendarIcon, BriefcaseIcon, BuildingIcon, ClockIcon, EditIcon, CakeIcon, TrendingUpIcon, AwardIcon, TrophyIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../hooks/useProgress';
import { BadgeCard } from '../components/BadgeCard';
import { CertificateCard } from '../components/CertificateCard';
import { BadgeDetailModal } from '../components/BadgeDetailModal';
import { CertificateDetailModal } from '../components/CertificateDetailModal';
export function Profile() {
  const {
    user,
    logout,
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  const {
    getCourseProgress,
    getAllBadges,
    getAllCertificates
  } = useProgress();
  const [isLoading, setIsLoading] = useState(true);
  // Badges & Certificates State
  const badges = getAllBadges().filter(b => b.isEarned);
  const certificates = getAllCertificates().filter(c => c.isEarned);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [isBadgeModalOpen, setIsBadgeModalOpen] = useState(false);
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/login');
    }
  }, [isLoading, isAuthenticated, navigate]);
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  if (isLoading) {
    return <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center" data-id="element-8816">
        <div className="text-center" data-id="element-8817">
          <div className="w-16 h-16 border-4 border-[#357BEB] border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-8818"></div>
          <p className="text-gray-600" data-id="element-8819">Se încarcă profilul...</p>
        </div>
      </div>;
  }
  if (!user) {
    return null;
  }
  // Check Course 1 completion
  const course1Progress = getCourseProgress('concept-clc', 6);
  const hasCompletedCourse1 = course1Progress === 100;
  // Check onboarding completion
  const hasCompletedOnboarding = !!(user.fullName && user.phone && user.dateOfBirth && user.city && user.address);
  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string): number => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birthDate.getDate()) {
      age--;
    }
    return age;
  };
  // Calculate account seniority (mock - in real app would use createdAt from database)
  const calculateSeniority = (): string => {
    // For demo purposes, using a fixed date or current date
    // In production, this would come from user.createdAt
    const accountCreatedDate = new Date('2024-01-15'); // Mock date
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - accountCreatedDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 30) {
      return `${diffDays} zile`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'lună' : 'luni'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor(diffDays % 365 / 30);
      if (remainingMonths === 0) {
        return `${years} ${years === 1 ? 'an' : 'ani'}`;
      }
      return `${years} ${years === 1 ? 'an' : 'ani'} și ${remainingMonths} ${remainingMonths === 1 ? 'lună' : 'luni'}`;
    }
  };
  // Format availability text
  const getAvailabilityText = () => {
    if (user.availability === 'full-time') return 'Full time';
    if (user.availability === 'part-time') return `Part time (${user.hoursPerWeek || 0} ore/săptămână)`;
    return 'Nespecificat';
  };
  // Format employment status text
  const getEmploymentStatusText = () => {
    if (user.employmentStatus === 'has-job') return 'Lucrez la alt job';
    if (user.employmentStatus === 'in-notice') return `În preaviz (până la ${user.noticeEndDate || 'nespecificat'})`;
    if (user.employmentStatus === 'no-job') return 'Nu am alt job';
    return 'Nespecificat';
  };
  // Format locality text
  const getLocalityText = () => {
    const localityMap: Record<string, string> = {
      'sector-1': 'Sector 1',
      'sector-2': 'Sector 2',
      'sector-3': 'Sector 3',
      'sector-4': 'Sector 4',
      'sector-5': 'Sector 5',
      'sector-6': 'Sector 6',
      ilfov: 'Ilfov',
      alte: 'Alte'
    };
    return localityMap[user.locality || ''] || user.locality || 'Nespecificat';
  };
  const userAge = user.dateOfBirth ? calculateAge(user.dateOfBirth) : null;
  const accountSeniority = calculateSeniority();
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-8820">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278]" data-id="element-8821">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-8822" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-8823"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-8824">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center" data-id="element-8825">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="inline-flex items-center justify-center w-32 h-32 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-6 shadow-2xl ring-4 ring-white/20" data-id="element-8826">
              {user.photo ? <img src={user.photo} alt="Profile" className="w-32 h-32 rounded-3xl object-cover" data-id="element-8827" /> : <UserIcon className="w-16 h-16 text-white" data-id="element-8828" />}
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight" data-id="element-8829">
              {user.fullName || 'Profilul Meu'}
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto" data-id="element-8830">
              Gestionează-ți contul și continuă parcursul de dezvoltare
            </p>
          </motion.div>
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-8831">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-12" data-id="element-8832">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="rgb(248 250 252)" data-id="element-8833" />
          </svg>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16" data-id="element-8834">
        {/* Profile Data Card - Only show if onboarding completed */}
        {hasCompletedOnboarding && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200 mb-8" data-id="element-8835">
            <div className="flex items-center justify-between mb-6" data-id="element-8836">
              <h2 className="text-2xl font-bold text-[#132C53] flex items-center" data-id="element-8837">
                <UserIcon className="w-6 h-6 mr-3 text-[#357BEB]" data-id="element-8838" />
                Date Profil
              </h2>
              <button onClick={() => navigate('/onboarding')} className="flex items-center space-x-2 text-[#357BEB] hover:text-[#2868d8] font-semibold transition-colors" data-id="element-8839">
                <EditIcon className="w-4 h-4" data-id="element-8840" />
                <span className="text-sm" data-id="element-8841">Editează</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8842">
              {/* Full Name */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-100" data-id="element-8843">
                <div className="flex items-start space-x-3" data-id="element-8844">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-8845">
                    <UserIcon className="w-5 h-5 text-white" data-id="element-8846" />
                  </div>
                  <div className="flex-1" data-id="element-8847">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8848">
                      Nume Complet
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8849">
                      {user.fullName}
                    </p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border-2 border-indigo-100" data-id="element-8850">
                <div className="flex items-start space-x-3" data-id="element-8851">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-8852">
                    <MailIcon className="w-5 h-5 text-white" data-id="element-8853" />
                  </div>
                  <div className="flex-1" data-id="element-8854">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8855">
                      Email
                    </p>
                    <p className="text-base font-bold text-[#132C53] break-all" data-id="element-8856">
                      {user.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border-2 border-emerald-100" data-id="element-8857">
                <div className="flex items-start space-x-3" data-id="element-8858">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0" data-id="element-8859">
                    <PhoneIcon className="w-5 h-5 text-white" data-id="element-8860" />
                  </div>
                  <div className="flex-1" data-id="element-8861">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8862">
                      Telefon
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8863">
                      {user.phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Date of Birth with Age */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border-2 border-purple-100" data-id="element-8864">
                <div className="flex items-start space-x-3" data-id="element-8865">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0" data-id="element-8866">
                    <CakeIcon className="w-5 h-5 text-white" data-id="element-8867" />
                  </div>
                  <div className="flex-1" data-id="element-8868">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8869">
                      Data Nașterii
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8870">
                      {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('ro-RO') : 'Nespecificat'}
                    </p>
                    {userAge && <p className="text-xs text-purple-600 font-semibold mt-1" data-id="element-8871">
                        {userAge} ani
                      </p>}
                  </div>
                </div>
              </div>

              {/* Account Seniority */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 border-2 border-amber-100" data-id="element-8872">
                <div className="flex items-start space-x-3" data-id="element-8873">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center flex-shrink-0" data-id="element-8874">
                    <TrendingUpIcon className="w-5 h-5 text-white" data-id="element-8875" />
                  </div>
                  <div className="flex-1" data-id="element-8876">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8877">
                      Vechime Cont
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8878">
                      {accountSeniority}
                    </p>
                    <p className="text-xs text-amber-600 font-semibold mt-1" data-id="element-8879">
                      Membru din 15.01.2024
                    </p>
                  </div>
                </div>
              </div>

              {/* City */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-5 border-2 border-orange-100" data-id="element-8880">
                <div className="flex items-start space-x-3" data-id="element-8881">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center flex-shrink-0" data-id="element-8882">
                    <MapPinIcon className="w-5 h-5 text-white" data-id="element-8883" />
                  </div>
                  <div className="flex-1" data-id="element-8884">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8885">
                      Oraș
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8886">
                      {user.city}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address - Full Width */}
              <div className="md:col-span-2 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-5 border-2 border-slate-200" data-id="element-8887">
                <div className="flex items-start space-x-3" data-id="element-8888">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-600 to-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-8889">
                    <MapPinIcon className="w-5 h-5 text-white" data-id="element-8890" />
                  </div>
                  <div className="flex-1" data-id="element-8891">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8892">
                      Adresă Completă
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8893">
                      {user.address}, {getLocalityText()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Availability */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-5 border-2 border-cyan-100" data-id="element-8894">
                <div className="flex items-start space-x-3" data-id="element-8895">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-8896">
                    <ClockIcon className="w-5 h-5 text-white" data-id="element-8897" />
                  </div>
                  <div className="flex-1" data-id="element-8898">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8899">
                      Disponibilitate
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8900">
                      {getAvailabilityText()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Employment Status */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 border-2 border-teal-100" data-id="element-8901">
                <div className="flex items-start space-x-3" data-id="element-8902">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-8903">
                    <BriefcaseIcon className="w-5 h-5 text-white" data-id="element-8904" />
                  </div>
                  <div className="flex-1" data-id="element-8905">
                    <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8906">
                      Situație Profesională
                    </p>
                    <p className="text-base font-bold text-[#132C53]" data-id="element-8907">
                      {getEmploymentStatusText()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Info - Only if has company */}
              {user.hasCompany && <div className="md:col-span-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border-2 border-indigo-100" data-id="element-8908">
                  <div className="flex items-start space-x-3" data-id="element-8909">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-8910">
                      <BuildingIcon className="w-5 h-5 text-white" data-id="element-8911" />
                    </div>
                    <div className="flex-1" data-id="element-8912">
                      <p className="text-xs font-semibold text-gray-600 mb-1" data-id="element-8913">
                        Informații Firmă
                      </p>
                      <div className="space-y-1" data-id="element-8914">
                        {user.companyName && <p className="text-base font-bold text-[#132C53]" data-id="element-8915">
                            {user.companyName}
                          </p>}
                        <div className="flex flex-wrap gap-3 text-sm text-gray-700" data-id="element-8916">
                          {user.cui && <span data-id="element-8917">
                              CUI:{' '}
                              <span className="font-semibold" data-id="element-8918">{user.cui}</span>
                            </span>}
                          {user.caenCode && <span data-id="element-8919">
                              CAEN:{' '}
                              <span className="font-semibold" data-id="element-8920">
                                {user.caenCode}
                              </span>
                            </span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>}

        {/* Achievements Section - NEW */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200 mb-8" data-id="element-8921">
          <div className="flex items-center justify-between mb-6" data-id="element-8922">
            <h2 className="text-2xl font-bold text-[#132C53] flex items-center" data-id="element-8923">
              <AwardIcon className="w-6 h-6 mr-3 text-[#357BEB]" data-id="element-8924" />
              Realizări
            </h2>
            <Link to="/dashboard" className="flex items-center space-x-2 text-[#357BEB] hover:text-[#2868d8] font-semibold transition-colors" data-id="element-8925">
              <span className="text-sm" data-id="element-8926">Vezi toate</span>
              <ArrowRightIcon className="w-4 h-4" data-id="element-8927" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-8928">
            {/* Badges Column */}
            <div data-id="element-8929">
              <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center" data-id="element-8930">
                <StarIcon className="w-5 h-5 mr-2 text-amber-500" data-id="element-8931" />
                Insigne Recente
              </h3>

              {badges.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-8932">
                  {badges.slice(0, 6).map(badge => <BadgeCard key={badge.id} badge={badge} onClick={() => {
                setSelectedBadge(badge);
                setIsBadgeModalOpen(true);
              }} data-id="element-8933" />)}
                </div> : <div className="bg-gray-50 rounded-xl p-6 text-center border border-gray-100" data-id="element-8934">
                  <p className="text-gray-500 text-sm" data-id="element-8935">
                    Nu ai câștigat încă nicio insignă.
                  </p>
                  <Link to="/dashboard" className="text-[#357BEB] text-sm font-semibold mt-2 inline-block" data-id="element-8936">
                    Mergi la cursuri
                  </Link>
                </div>}
            </div>

            {/* Certificates Column */}
            <div data-id="element-8937">
              <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center" data-id="element-8938">
                <TrophyIcon className="w-5 h-5 mr-2 text-blue-500" data-id="element-8939" />
                Certificate
              </h3>

              {certificates.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8940">
                  {certificates.slice(0, 4).map(cert => <CertificateCard key={cert.id} certificate={cert} onClick={() => {
                setSelectedCertificate(cert);
                setIsCertModalOpen(true);
              }} data-id="element-8941" />)}
                </div> : <div className="bg-gray-50 rounded-xl p-6 text-center border border-gray-100" data-id="element-8942">
                  <p className="text-gray-500 text-sm" data-id="element-8943">
                    Nu ai obținut încă niciun certificat.
                  </p>
                  <Link to="/dashboard" className="text-[#357BEB] text-sm font-semibold mt-2 inline-block" data-id="element-8944">
                    Continuă învățarea
                  </Link>
                </div>}
            </div>
          </div>
        </motion.div>

        {/* Account Information Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200 mb-8" data-id="element-8945">
          <h2 className="text-2xl font-bold text-[#132C53] mb-6 flex items-center" data-id="element-8946">
            <ShieldCheckIcon className="w-6 h-6 mr-3 text-[#357BEB]" data-id="element-8947" />
            Securitate Cont
          </h2>

          <div className="space-y-6" data-id="element-8948">
            {/* Password Section */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border-2 border-gray-200" data-id="element-8949">
              <div className="flex items-start justify-between" data-id="element-8950">
                <div className="flex items-start space-x-4" data-id="element-8951">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-8952">
                    <KeyIcon className="w-6 h-6 text-white" data-id="element-8953" />
                  </div>
                  <div data-id="element-8954">
                    <p className="text-sm font-semibold text-gray-600 mb-1" data-id="element-8955">
                      Parolă
                    </p>
                    <p className="text-lg font-bold text-[#132C53] mb-2" data-id="element-8956">
                      ••••••••••••
                    </p>
                    <button className="text-sm text-[#357BEB] hover:text-[#2868d8] font-semibold flex items-center space-x-1 transition-colors" data-id="element-8957">
                      <LockIcon className="w-4 h-4" data-id="element-8958" />
                      <span data-id="element-8959">Schimbă parola</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Next Steps Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200 mb-8" data-id="element-8960">
          <h2 className="text-2xl font-bold text-[#132C53] mb-6 flex items-center" data-id="element-8961">
            <RocketIcon className="w-6 h-6 mr-3 text-[#21A366]" data-id="element-8962" />
            Pașii Următori
          </h2>

          {!hasCompletedCourse1 ?
        // Show Course 1 CTA
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-8 text-white relative overflow-hidden" data-id="element-8963">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" data-id="element-8964"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl" data-id="element-8965"></div>

              <div className="relative z-10" data-id="element-8966">
                <div className="flex items-start space-x-4 mb-6" data-id="element-8967">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-8968">
                    <BookOpenIcon className="w-8 h-8 text-white" data-id="element-8969" />
                  </div>
                  <div data-id="element-8970">
                    <h3 className="text-2xl font-bold mb-2" data-id="element-8971">
                      Finalizează Training 1
                    </h3>
                    <p className="text-blue-100 text-lg" data-id="element-8972">
                      Completează Training 1 – Concept CLC pentru a debloca
                      procesul de on-boarding
                    </p>
                  </div>
                </div>

                {course1Progress > 0 && <div className="mb-6" data-id="element-8973">
                    <div className="flex items-center justify-between text-sm mb-2" data-id="element-8974">
                      <span className="font-semibold" data-id="element-8975">Progres training</span>
                      <span className="font-bold" data-id="element-8976">{course1Progress}%</span>
                    </div>
                    <div className="bg-white/20 rounded-full h-3 overflow-hidden" data-id="element-8977">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${course1Progress}%`
                }} transition={{
                  duration: 1
                }} className="bg-white h-full rounded-full" data-id="element-8978" />
                    </div>
                  </div>}

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6" data-id="element-8979">
                  <h4 className="font-bold mb-3 flex items-center" data-id="element-8980">
                    <SparklesIcon className="w-5 h-5 mr-2" data-id="element-8981" />
                    Ce deblochezi după finalizare:
                  </h4>
                  <ul className="space-y-2" data-id="element-8982">
                    <li className="flex items-center space-x-2" data-id="element-8983">
                      <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" data-id="element-8984" />
                      <span data-id="element-8985">Acces la procesul de on-boarding</span>
                    </li>
                    <li className="flex items-center space-x-2" data-id="element-8986">
                      <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" data-id="element-8987" />
                      <span data-id="element-8988">Posibilitatea de a deveni Agent CLC</span>
                    </li>
                    <li className="flex items-center space-x-2" data-id="element-8989">
                      <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" data-id="element-8990" />
                      <span data-id="element-8991">Acces la training-urile următoare din program</span>
                    </li>
                  </ul>
                </div>

                <Link to="/curs/concept-clc" className="inline-flex items-center justify-center space-x-2 bg-white text-[#357BEB] px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105 w-full sm:w-auto" data-id="element-8992">
                  <span data-id="element-8993">
                    {course1Progress > 0 ? 'Continuă Training 1' : 'Începe Training 1'}
                  </span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-8994" />
                </Link>
              </div>
            </div> : !hasCompletedOnboarding ?
        // Show Onboarding CTA
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl p-8 text-white relative overflow-hidden" data-id="element-8995">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" data-id="element-8996"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl" data-id="element-8997"></div>

              <div className="relative z-10" data-id="element-8998">
                <div className="flex items-start space-x-4 mb-6" data-id="element-8999">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-9000">
                    <CheckCircleIcon className="w-8 h-8 text-white" data-id="element-9001" />
                  </div>
                  <div data-id="element-9002">
                    <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full mb-3" data-id="element-9003">
                      <CheckCircleIcon className="w-4 h-4" data-id="element-9004" />
                      <span className="text-sm font-semibold" data-id="element-9005">
                        Training 1 Finalizat
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold mb-2" data-id="element-9006">
                      Finalizează On-boarding
                    </h3>
                    <p className="text-green-100 text-lg" data-id="element-9007">
                      Completează procesul de on-boarding pentru a activa contul
                      și a accesa toate funcționalitățile
                    </p>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6" data-id="element-9008">
                  <h4 className="font-bold mb-3 flex items-center" data-id="element-9009">
                    <SparklesIcon className="w-5 h-5 mr-2" data-id="element-9010" />
                    Ce urmează:
                  </h4>
                  <ul className="space-y-2" data-id="element-9011">
                    <li className="flex items-center space-x-2" data-id="element-9012">
                      <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" data-id="element-9013" />
                      <span data-id="element-9014">Completează datele personale</span>
                    </li>
                    <li className="flex items-center space-x-2" data-id="element-9015">
                      <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" data-id="element-9016" />
                      <span data-id="element-9017">Configurează disponibilitatea</span>
                    </li>
                    <li className="flex items-center space-x-2" data-id="element-9018">
                      <CheckCircleIcon className="w-5 h-5 text-green-300 flex-shrink-0" data-id="element-9019" />
                      <span data-id="element-9020">Activează contul de agent</span>
                    </li>
                  </ul>
                </div>

                <Link to="/onboarding" className="inline-flex items-center justify-center space-x-2 bg-white text-[#21A366] px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105 w-full sm:w-auto" data-id="element-9021">
                  <span data-id="element-9022">Începe On-boarding</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-9023" />
                </Link>
              </div>
            </div> :
        // Both completed - show success state
        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-8 text-white relative overflow-hidden" data-id="element-9024">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" data-id="element-9025"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl" data-id="element-9026"></div>

              <div className="relative z-10 text-center" data-id="element-9027">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6" data-id="element-9028">
                  <CheckCircleIcon className="w-10 h-10 text-white" data-id="element-9029" />
                </div>
                <h3 className="text-3xl font-bold mb-3" data-id="element-9030">
                  Cont Complet Configurat!
                </h3>
                <p className="text-purple-100 text-lg mb-6" data-id="element-9031">
                  Ai finalizat cu succes training-ul și on-boarding-ul. Acum
                  poți accesa toate funcționalitățile platformei.
                </p>
                <Link to="/dashboard" className="inline-flex items-center justify-center space-x-2 bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105" data-id="element-9032">
                  <span data-id="element-9033">Mergi la Dashboard</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-9034" />
                </Link>
              </div>
            </div>}
        </motion.div>

        {/* Logout Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white rounded-3xl p-6 shadow-lg border border-gray-200" data-id="element-9035">
          <button onClick={handleLogout} className="flex items-center space-x-3 text-red-500 hover:text-red-700 transition-colors w-full justify-center sm:justify-start" data-id="element-9036">
            <LogOutIcon className="w-5 h-5" data-id="element-9037" />
            <span className="font-semibold" data-id="element-9038">Deconectare</span>
          </button>
        </motion.div>
      </div>

      {/* Modals */}
      <BadgeDetailModal badge={selectedBadge} isOpen={isBadgeModalOpen} onClose={() => setIsBadgeModalOpen(false)} data-id="element-9039" />
      <CertificateDetailModal certificate={selectedCertificate} isOpen={isCertModalOpen} onClose={() => setIsCertModalOpen(false)} data-id="element-9040" />
    </div>;
}